import { useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { queryKeys } from "@nasam/kernel";
import { useDebouncedValue } from "../../../shared/application/useDebouncedValue";
import type { CompaniesListQuery, CompaniesRepository } from "../domain";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export interface CompanyOption {
  readonly value: string;
  readonly label: string;
}

export interface UseCompanyOptionsResult {
  readonly options: readonly CompanyOption[];
  readonly searchValue: string;
  readonly onSearchChange: (value: string) => void;
  readonly isLoading: boolean;
  readonly isFetchingMore: boolean;
  readonly hasMore: boolean;
  readonly onLoadMore: () => void;
}

/**
 * Server-searched, paginated company options for the `Combobox` async mode —
 * unlike the plain `useCompaniesQuery(repository, { limit: 100, ... })` used
 * elsewhere, this never caps the reachable set at one page.
 * `repository` is optional to match call sites where it's genuinely absent
 * (e.g. `SubscriptionFormScreen`, which renders without it in some setups).
 */
export function useCompanyOptions(
  repository: CompaniesRepository | undefined,
): UseCompanyOptionsResult {
  const { i18n } = useTranslation();
  const isAr = i18n.language.startsWith("ar");
  const sortBy = isAr ? "nameAr" : "nameEn";

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);
  const search =
    debouncedSearch.trim() === "" ? undefined : debouncedSearch.trim();

  const isEnabled =
    repository !== undefined && typeof repository.list === "function";

  const listQuery = (page: number): CompaniesListQuery => ({
    page,
    limit: PAGE_SIZE,
    sort: "asc",
    sortBy,
    ...(search === undefined ? {} : { search }),
  });

  const query = useInfiniteQuery({
    queryKey: queryKeys.companies.list({
      limit: PAGE_SIZE,
      search,
      sort: "asc",
      sortBy,
    }),
    queryFn: ({ pageParam }) => repository!.list(listQuery(pageParam)),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.currentPage < lastPage.meta.totalPages
        ? lastPage.meta.currentPage + 1
        : undefined,
    enabled: isEnabled,
  });

  const options = useMemo(
    () =>
      (query.data?.pages ?? []).flatMap((page) =>
        page.data.map((company) => ({
          value: company.id,
          label: isAr
            ? company.nameAr || company.nameEn
            : company.nameEn || company.nameAr,
        })),
      ),
    [query.data, isAr],
  );

  return {
    options,
    searchValue: searchTerm,
    onSearchChange: setSearchTerm,
    isLoading: query.isLoading,
    isFetchingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage,
    onLoadMore: () => {
      void query.fetchNextPage();
    },
  };
}
